<template>
  <div class="app-container">
    <el-card>
      <div slot="header" class="clearfix">
        <span>SKU管理</span>
      </div>
      
      <el-alert
        title="提示：SKU管理用于查看和管理商品的规格和库存信息"
        type="info"
        :closable="false"
        style="margin-bottom: 20px"
      />

      <!-- 搜索区域 -->
      <el-form :inline="true" :model="queryParams" ref="queryForm" class="demo-form-inline">
        <el-form-item label="商品名称">
          <el-input 
            v-model="queryParams.goodsName" 
            placeholder="请输入商品名称" 
            clearable 
            size="small"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item label="SKU编码">
          <el-input 
            v-model="queryParams.skuSn" 
            placeholder="请输入SKU编码" 
            clearable 
            size="small"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" size="small" @click="handleQuery">搜索</el-button>
          <el-button icon="el-icon-refresh" size="small" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 数据表格 -->
      <el-table v-loading="loading" :data="skuList">
        <el-table-column label="SKU ID" align="center" prop="id" width="100" />
        <el-table-column label="商品名称" align="center" prop="goodsName" show-overflow-tooltip min-width="150" />
        <el-table-column label="规格组合" align="center" prop="specText" show-overflow-tooltip min-width="200" />
        <el-table-column label="价格(元)" align="center" prop="price" width="100">
          <template slot-scope="scope">
            <span style="color: #f56c6c; font-weight: bold;">¥{{ scope.row.price }}</span>
          </template>
        </el-table-column>
        <el-table-column label="库存" align="center" prop="stock" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.stock > 0 ? 'success' : 'danger'">
              {{ scope.row.stock }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="销量" align="center" prop="sales" width="100" />
        <el-table-column label="SKU编码" align="center" prop="skuSn" width="150" show-overflow-tooltip />
        <el-table-column label="状态" align="center" prop="status" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" icon="el-icon-edit" @click="handleUpdate(scope.row)">修改</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <pagination
        v-show="total > 0"
        :total="total"
        :page.sync="queryParams.current"
        :limit.sync="queryParams.size"
        @pagination="getList"
      />
    </el-card>

    <!-- 修改SKU对话框 -->
    <el-dialog title="修改SKU" :visible.sync="dialogVisible" width="500px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="商品名称">
          <el-input v-model="form.goodsName" disabled />
        </el-form-item>
        <el-form-item label="规格组合">
          <el-input v-model="form.specText" disabled />
        </el-form-item>
        <el-form-item label="SKU编码" prop="skuSn">
          <el-input v-model="form.skuSn" placeholder="请输入SKU编码（可选）" />
        </el-form-item>
        <el-form-item label="价格" prop="price">
          <el-input-number v-model="form.price" :min="0" :precision="2" controls-position="right" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="库存" prop="stock">
          <el-input-number v-model="form.stock" :min="0" controls-position="right" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="submitForm">确 定</el-button>
        <el-button @click="cancel">取 消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getSkuPage, updateSku } from '@/api/ordering/goods'

export default {
  name: 'GoodsSku',
  data() {
    return {
      loading: true,
      total: 0,
      skuList: [],
      queryParams: {
        current: 1,
        size: 10,
        goodsName: undefined,
        skuSn: undefined
      },
      // 对话框相关
      dialogVisible: false,
      form: {
        id: null,
        goodsName: '',
        specText: '',
        skuSn: '',
        price: 0,
        stock: 0,
        status: 1
      },
      rules: {
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' }
        ],
        stock: [
          { required: true, message: '请输入库存', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    /** 查询SKU列表 */
    async getList() {
      this.loading = true
      try {
        const params = {
          current: this.queryParams.current,
          size: this.queryParams.size
        }
        
        // 添加搜索条件
        if (this.queryParams.goodsName) {
          params.goodsName = this.queryParams.goodsName
        }
        if (this.queryParams.skuSn) {
          params.skuSn = this.queryParams.skuSn
        }
        
        const res = await getSkuPage(params)
        this.skuList = res.records || []
        this.total = res.total || 0
      } catch (error) {
        console.error('获取SKU列表失败', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },
    /** 搜索按钮操作 */
    handleQuery() {
      this.queryParams.current = 1
      this.getList()
    },
    /** 重置按钮操作 */
    resetQuery() {
      if (this.$refs.queryForm) {
        this.$refs.queryForm.resetFields()
      }
      this.handleQuery()
    },
    /** 修改按钮操作 */
    handleUpdate(row) {
      this.form = {
        id: row.id,
        goodsName: row.goodsName,
        specText: row.specText,
        skuSn: row.skuSn || '',
        price: row.price,
        stock: row.stock,
        status: row.status
      }
      this.dialogVisible = true
    },
    /** 提交表单 */
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          try {
            await updateSku(this.form)
            this.$message.success('修改成功')
            this.dialogVisible = false
            this.getList()
          } catch (error) {
            console.error('保存失败', error)
            this.$message.error('保存失败')
          }
        }
      })
    },
    /** 取消按钮 */
    cancel() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.app-container {
  padding: 20px;
}

/* 弹窗内容间距优化 */
::v-deep .el-dialog__body {
  padding: 20px 30px;
}

/* 弹窗头部间距 */
::v-deep .el-dialog__header {
  padding: 20px 30px 15px;
  margin-right: 0;
}

/* 弹窗底部间距 */
::v-deep .el-dialog__footer {
  padding: 15px 30px 20px;
}

/* 表单项间距 */
::v-deep .el-form-item {
  margin-bottom: 20px;
}
</style>
